import { useEffect, useRef, useState } from "react";
import EventEHRLazyDropdown from "../../Molecules/lazy-loading-search/EventEHRLazyLoadingSearch";
import PatientListService from "../../../services/PatientListService";
import { bottomDirectionArrow } from "../../../assets";

interface LocationOption {
  label: string;
  value: string;
}

interface IncidentLocationSelectProps {
  // The currently selected incident-location name (the form stores the name).
  value: string;
  // Called with the selected location name (or "" on clear).
  onChange: (val: string) => void;
  onBlur?: (e: any) => void;
  error?: boolean;
  helperText?: string;
  isDisabled?: boolean;
}

/**
 * Incident-location selector that lazy-loads the master-data list on scroll,
 * mirroring the `FacilitySelect` pattern. It owns its own pagination state and
 * uses the shared `EventEHRLazyDropdown`; typing filters the loaded options
 * client-side (MUI default) while scrolling to the bottom loads the next page.
 * This removes the old hard cap where only the first 100 locations were shown.
 */
const IncidentLocationSelect = ({
  value,
  onChange,
  onBlur = (e) => {},
  error = false,
  helperText,
  isDisabled = false,
}: IncidentLocationSelectProps) => {
  const [pageCount, setPageCount] = useState(1);
  const [dropdownSearch, setDropdownSearch] = useState("");
  const [dropdownOptions, setDropdownOptions] = useState<LocationOption[]>([]);
  const [resetFlag, setResetFlag] = useState(false);
  // The form stores the location name, which is also the option label/value, so
  // we can derive the selected option from the incoming `value` prop.
  const [selectedValue, setSelectedValue] = useState<LocationOption | null>(
    value ? { label: value, value } : null
  );
  // Whether there are more pages to load. Read synchronously (ref, not state)
  // so the scroll-driven pageCount bump can't trigger a fetch past the last page.
  const hasMoreRef = useRef(true);

  // Keep the displayed selection in sync with the form value (edit prefill / reset).
  useEffect(() => {
    setSelectedValue(value ? { label: value, value } : null);
  }, [value]);

  // Browse the incident-location list, paginated (appends on scroll). The
  // resetFlag guard mirrors FacilitySelect so we don't double-fetch page 1.
  useEffect(() => {
    if (!resetFlag) {
      getDropdownList(pageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageCount]);

  useEffect(() => {
    if (resetFlag) {
      hasMoreRef.current = true;
      setPageCount(1);
      getDropdownList(1, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetFlag]);

  const mapLocations = (rows: any[]): LocationOption[] =>
    rows
      ?.filter((row: any) => row?.status === "Active" && row?.name)
      .map((row: any) => ({ label: row.name, value: row.name })) || [];

  const getDropdownList = async (page: any, reset = false) => {
    // Don't fetch past the last page (the scroll handler may still bump
    // pageCount once we're at the bottom of a single-page list).
    if (!reset && page > 1 && !hasMoreRef.current) {
      return;
    }
    try {
      const response = await PatientListService.getMasterDataByType({
        type: "incident_location",
        pageNumber: page,
        limit: 100,
      });
      const rows = response?.data?.masterData || [];
      hasMoreRef.current = page < (response?.data?.totalPages ?? 1);
      const options = mapLocations(rows);
      if (options.length > 0) {
        if (reset) {
          setDropdownOptions(options);
          setResetFlag(false);
        } else if (
          !dropdownOptions?.some((o) => o?.value === options[0]?.value)
        ) {
          // Append the next page (dedupe by checking the first new row).
          setDropdownOptions(dropdownOptions.concat(options));
        }
      } else if (reset) {
        setResetFlag(false);
      }
    } catch (error) {
      console.log("error:", error);
    }
  };

  return (
    <EventEHRLazyDropdown
      label="Select Incident Location"
      placeholder="Select Incident Location"
      isDisabled={isDisabled}
      error={error}
      helperText={helperText}
      onBlur={onBlur}
      noOptionText="No location found"
      dropdownOptions={dropdownOptions}
      dropdownSearch={dropdownSearch}
      selectedValue={selectedValue}
      fullWidth="100%"
      dropdownIcon={true}
      endIcon={
        <img
          alt="dropdown-arrow"
          src={bottomDirectionArrow}
          width={"14px"}
          height={"14px"}
        />
      }
      setDropdownSearch={setDropdownSearch}
      setPageCount={setPageCount}
      setResetFlag={setResetFlag}
      setSelectedValue={(data) => {
        setSelectedValue(data ?? null);
        onChange(data?.value || "");
      }}
    />
  );
};

export default IncidentLocationSelect;
